import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Stack,
  Divider,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { useUserSettings } from '../hooks/useUserSettings';
import { useTheme } from '../lib/themeContext';
import toast from 'react-hot-toast';
import { useBackendSettings } from '../hooks/useBackendSettings';
import { useDeleteAccount } from '../hooks/useDeleteAccount';

export const Settings = () => {
  const { t } = useTranslation('settings');
  const { user } = useAuthStore();
  const { isUpdating, updateProfile, updatePassword, uploadProfilePicture } = useUserSettings();
  const { mode, setMode } = useTheme();
  const { settings, saveSettings } = useBackendSettings();
  const { deleteAccount, isDeleting } = useDeleteAccount();
  
  // Profile settings
  const [displayName, setDisplayName] = useState(user?.user_metadata?.display_name || '');
  const [email, setEmail] = useState(user?.email || '');
  
  // App preferences
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(mode);
  const [emailNotifications, setEmailNotifications] = useState(settings?.email_notifications === 'true');
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Delete account dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleSaveProfile = async () => {
    const result = await updateProfile({
      displayName,
      email,
    });
    
    if (result.success && email !== user?.email) {
      toast(t('emailChangeNotice'), { icon: 'ℹ️' });
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t('passwordsNotMatch'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('passwordTooShort'));
      return;
    }

    const result = await updatePassword(currentPassword, newPassword);
    
    if (result.success) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleProfilePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await uploadProfilePicture(file);
  };

  const handleSavePreferences = async () => {
    console.log('Saving preferences - theme:', theme, 'emailNotifications:', emailNotifications);
    
    // Update theme preference locally
    setMode(theme);
    
    // Save preferences to backend
    const preferencesToSave: Record<string, string> = {
      theme_preference: theme,
      email_notifications: emailNotifications ? 'true' : 'false',
    };
    
    saveSettings(preferencesToSave);
  };

  const handleDeleteAccount = async () => {
    const result = await deleteAccount();
    if (result.success) {
      setDeleteDialogOpen(false);
      // Navigation is handled by the hook
    }
    // Error toast is already handled by the hook
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('settings')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('manageAccount')}
      </Typography>

      <Stack spacing={3}>
        {/* Profile Settings */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('profileInformation')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* Profile Picture */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={user?.user_metadata?.avatar_url}
                  sx={{ width: 80, height: 80 }}
                >
                  {displayName?.[0]?.toUpperCase() || email?.[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('profilePicture')}
                  </Typography>
                  <IconButton component="label" color="primary" size="small" disabled={isUpdating}>
                    <PhotoCameraIcon />
                    <input 
                      type="file" 
                      hidden 
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                    />
                  </IconButton>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('profilePictureFormats')}
                  </Typography>
                </Box>
              </Box>

              {/* Display Name */}
              <TextField
                label={t('displayName')}
                fullWidth
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />

              {/* Email */}
              <TextField
                label={t('email')}
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                helperText={t('emailHelper')}
              />

              <Box>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveProfile}
                  disabled={isUpdating}
                >
                  {isUpdating ? t('saving') : t('saveChanges')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Password Change */}
        {user?.app_metadata?.provider === 'email' && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('changePassword')}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Stack spacing={2}>
                <TextField
                  label={t('currentPassword')}
                  type="password"
                  fullWidth
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <TextField
                  label={t('newPassword')}
                  type="password"
                  fullWidth
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  helperText={t('newPasswordHelper')}
                />
                <TextField
                  label={t('confirmNewPassword')}
                  type="password"
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <Box>
                  <Button
                    variant="contained"
                    onClick={handleChangePassword}
                    disabled={isUpdating || !currentPassword || !newPassword || !confirmPassword}
                  >
                    {isUpdating ? t('changing') : t('changePasswordButton')}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* App Preferences */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('appPreferences')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* Theme */}
              <FormControl fullWidth>
                <InputLabel>{t('theme')}</InputLabel>
                <Select
                  value={theme}
                  label={t('theme')}
                  onChange={(e) => setTheme(e.target.value as 'light' | 'dark' | 'system')}
                >
                  <MenuItem value="light">{t('themeLight')}</MenuItem>
                  <MenuItem value="dark">{t('themeDark')}</MenuItem>
                  <MenuItem value="system">{t('themeSystem')}</MenuItem>
                </Select>
              </FormControl>

              {/* Email Notifications */}
              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                  />
                }
                label={t('emailNotifications')}
              />

              <Box>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSavePreferences}
                >
                  {t('savePreferences')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Account Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('accountInformation')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('accountId')}
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {user?.id || t('notAvailable')}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('authProvider')}
                </Typography>
                <Typography variant="body1">
                  {user?.app_metadata?.provider === 'email' ? t('authProviderEmail') : 
                   user?.app_metadata?.provider || t('notAvailable')}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('memberSince')}
                </Typography>
                <Typography variant="body1">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : t('notAvailable')}
                </Typography>
              </Paper>
            </Stack>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card sx={{ borderColor: 'error.main' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="error">
              {t('dangerZone')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('deleteAccountWarning')}
            </Alert>

            <Button 
              variant="outlined" 
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isDeleting}
            >
              {t('deleteAccount')}
            </Button>
          </CardContent>
        </Card>
      </Stack>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('deleteAccountConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('deleteAccountConfirmMessage')}
          </DialogContentText>
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('deleteAccountFinalWarning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting ? t('deleting') : t('deleteAccountConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
